//replaces DashboardHeader.tsx
import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import noop from "lodash/noop";
import { useDispatch, useSelector } from "react-redux";

import { Pdf, Ppt, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, ButtonLabel, IconButton } from "@similarweb/ui-components";
import { Dropdown, EllipsisDropdownItem, SimpleDropdownItem } from "@similarweb/ui-components";
import { EllipsisDashboardHeaderContainer } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { showErrorToast } from "app/actions/toast_actions";
import userEngagementResource from "app/common/resources/userEngagementResource";
import { swSettings } from "app/common/services/swSettings";
import DashboardService from "app/components/dashboard/services/DashboardService";
import type { IDashboard } from "app/components/dashboard/services/IDashboard";
import { setSharedWithMeDashboards } from "app/pages/dashboard/DashboardSideNavActions";
import { setDashboardTitle } from "app/pages/dashboard/redux/actions/CurrentDashboardActions";
import { widgetsLoadedSelector } from "app/pages/dashboard/redux/DashboardSelectors";
import SharedDashboardHeaderButtonTooltipWrap from "app/pages/dashboard/ShareDashboardHeaderButtonTooltipWrap";
import { ShareDashboardService } from "app/pages/dashboard/ShareDashboardService";
import { SwTrack } from "app/services/SwTrack";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { NotificationButton } from "pro-features/components/Buttons/src/NotificationButton";
import { ShareDashboardButton } from "pro-features/components/Buttons/src/ShareDashboardButton";
import { ConfirmationTextModal } from "pro-features/components/Modals/src/ConfirmationTextModal";
import I18n, { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import DashboardRenameDialog from "../DashboardRenameDialog";
import { useSharedUsersListContext } from "../hooks/useSharedUsersList";

import { DashboardButtons, DashboardHeaderContainer, EllipsisIconContainer, IconItem } from "./DashboardHeaderStyles";
import { DashboardSearchHeader } from "./DashboardSearchHeader";
import { allTrackers } from "app/services/track/track";

interface IDashboardHeaderV2 {
    dashboard: IDashboard;
    duplicateDashboard: () => void;
    downloadPdf: () => void;
    exportToPpt: () => void;
    shareDashboard: () => void;
    openMetricsGallery: () => void;
    isSubscriptionOn: boolean;
    isNotificationAllowed: boolean;
    onSubscription: () => void;
    onUnsubscription: () => void;
    isDuplicatingEnabled?: boolean;
    isSharedWithMe?: boolean;
    navigateToDashboard: (dashboardId: string) => void;
    isSharingDashboard: boolean;
}

const DASHBOARD_NOTIFICATIONS_ENG_TYPE = "dashboard_notifications_initial";

export const DashboardHeaderV2 = ({
    dashboard,
    duplicateDashboard,
    downloadPdf,
    exportToPpt,
    shareDashboard,
    openMetricsGallery,
    isSubscriptionOn,
    isNotificationAllowed,
    onSubscription,
    onUnsubscription,
    navigateToDashboard,
    isSharingDashboard,
}: IDashboardHeaderV2): JSX.Element => {
    const dashboardId = dashboard?.id;
    const sharedWithAccounts = dashboard?.sharedWithAccounts || [];
    const isSharedByMe = dashboard?.isSharedByMe;
    const isSharedWithMe = dashboard?.isSharedWithMe;
    const isSharingEnabled = swSettings.components.ShareDashboard.resources.CanShareADashboard;
    const isDuplicatingEnabled = swSettings.components.Dashboard.resources.CanDuplicateADashboard;

    const readOnly = dashboard?.readOnly;

    const dispatch = useDispatch();
    const t = useTranslation();
    const isTrial = useRef(isTrialOrAfterTrial());
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [promotionTooltip, setPromotionTooltip] = useState<boolean>(false);
    const [deletePopUpIsOpen, setDeletePopUpIsOpen] = useState<boolean>(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState<boolean>(false);

    const [{ dashboardOwnerName: ownerName, isLoading: isUsersLoading, viewers: dashboardViewers }] =
        useSharedUsersListContext();

    useEffect(() => {
        async function doAsync() {
            await shouldExposeUserToTemplates();
        }
        doAsync();
    }, []);

    const widgetsLoaded = useSelector(widgetsLoadedSelector);
    const currentTitle = useSelector((state) => state.customDashboard.currentDashboard.dashboardTitle);

    const services = useMemo(() => {
        return {
            swNavigator: proNavigator,
        };
    }, []);

    const onExportMenuItemClick = (item) => {
        switch (item.id) {
            case "ppt":
                allTrackers.dispatchTrackEvent("Drop Down", "click", "Export PPT");
                SwTrack.all.trackEvent("Dashboard", "click", "Dashboard Header/PPT");
                exportToPpt();
                break;

            case "pdf":
                allTrackers.dispatchTrackEvent("Drop Down", "click", "Export PDF");
                SwTrack.all.trackEvent("Dashboard", "click", "Dashboard Header/PDF");
                downloadPdf();
                break;
        }
    };

    async function shouldExposeUserToTemplates() {
        const userEngagements = await userEngagementResource.getAll();
        const didUserExposed = userEngagements.hasOwnProperty(DASHBOARD_NOTIFICATIONS_ENG_TYPE);
        setPromotionTooltip(!didUserExposed);
        if (!didUserExposed) {
            userEngagementResource.logEngagement({
                engType: DASHBOARD_NOTIFICATIONS_ENG_TYPE,
            });
        }
    }

    function revertDashboardName() {
        setIsRenameDialogOpen(false);
    }

    /**
     * Return a button component wrapped with tooltip in case the dashboard is shared with me,
     * suggest user to duplicate
     */
    function getHeaderButton(type, buttonComponent) {
        if (isSharedWithMe) {
            return isDuplicatingEnabled ? (
                <SharedDashboardHeaderButtonTooltipWrap
                    onClick={duplicateDashboard}
                    ownerName={ownerName}
                    type={type}
                >
                    <div>{buttonComponent}</div>
                </SharedDashboardHeaderButtonTooltipWrap>
            ) : null;
        } else {
            return buttonComponent;
        }
    }

    async function renameDashboard(newName: string) {
        if (newName !== currentTitle) {
            if (newName.trim() === "") {
                revertDashboardName();
            } else {
                try {
                    await DashboardService.renameDashboard({
                        id: dashboardId,
                        title: newName,
                    });
                    dispatch(setDashboardTitle(dashboardId, newName));
                } catch {
                    dispatch(showErrorToast(t("dashboard.rename.failed.toast")));
                    revertDashboardName();
                }
            }
        }
        setIsRenameDialogOpen(false);
    }

    const renderHeaderTitle = (): JSX.Element => {
        const dashboardList = DashboardService.dashboards.map((dashboard) => {
            return {
                id: dashboard.id,
                title: dashboard.title,
            };
        });

        const clickHandler = (item) => {
            navigateToDashboard(item.id);
        };

        return (
            <DashboardSearchHeader
                dashboardList={dashboardList}
                clickHandler={clickHandler}
                currentDashboardId={dashboardId}
            />
        );
    };

    const renderAddMetricButton = (): JSX.Element =>
        getHeaderButton(
            "add metric button",
            <Button
                isDisabled={readOnly}
                onClick={openMetricsGallery}
                type="flat"
                data-automation-i18n-key="home.dashboards.add.widget.2"
            >
                <ButtonLabel>
                    <I18n>home.dashboards.add.widget.2</I18n>
                </ButtonLabel>
            </Button>,
        );
    function openModal() {
        setIsModalOpen(true);
    }

    function onConfirmationCloseClick() {
        SwTrack.all.trackEvent("Dashboard subscription", "click", "popup/cancel");
        setIsModalOpen(false);
    }

    function onConfirmationApproveClick() {
        onUnsubscription();
        setIsModalOpen(false);
    }

    const renderSubscribeConfirmModal = (): JSX.Element => {
        return (
            <ConfirmationTextModal
                isOpen={isModalOpen}
                onCloseClick={onConfirmationCloseClick}
                onCancelClick={onConfirmationCloseClick}
                onApproveClick={onConfirmationApproveClick}
                cancelButtonText={t("dashboard.dashboardSubscription.ConfirmationModal.cancelButtonText")}
                approveButtonText={t("dashboard.dashboardSubscription.ConfirmationModal.approveButtonText")}
                headerText={t("dashboard.dashboardSubscription.ConfirmationModal.headerText")}
                contentText={t("dashboard.dashboardSubscription.ConfirmationModal.contentText")}
            />
        );
    };

    const renderSubscribeButton = (): JSX.Element => {
        const primaryToolTipText = t("dashboard.dashboardSubscription.subscribeButton.tooltip");
        const activatedToolTipText = t("dashboard.dashboardSubscription.unsubscribeButton.tooltip");
        const btnType = "flat";

        if (isNotificationAllowed) {
            if (isSubscriptionOn) {
                return (
                    <NotificationButton
                        isActivated={true}
                        onClick={openModal}
                        toolTipText={activatedToolTipText}
                        btnType={btnType}
                    />
                );
            } else {
                return (
                    <NotificationButton
                        promotionTooltip={promotionTooltip}
                        toolTipText={primaryToolTipText}
                        onClick={onSubscription}
                        btnType={btnType}
                    />
                );
            }
        } else {
            return <NotificationButton isDisabled={true} />;
        }
    };

    const handleShareDashboard = (): void => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Dashboard Header/Share");
        shareDashboard();
    };

    const ShareButton = () =>
        getHeaderButton(
            "share button",
            <ShareDashboardButton
                isDisabled={readOnly || !widgetsLoaded}
                onClick={handleShareDashboard}
                isDataLoading={isUsersLoading || !widgetsLoaded || isSharingDashboard}
                sharedWithAccounts={sharedWithAccounts}
                dashboardViewers={dashboardViewers}
            />,
        );

    function getExportMenuItems() {
        const hasAccessToPptExport = swSettings.user.hasPptExport;
        const menuButton = (
            <SimpleDropdownItem
                key={"main-item"}
                id={"main-item"}
            >
                <IconButton
                    type="primary"
                    iconName="upload"
                >
                    <ButtonLabel>
                        <I18n>home.dashboards.export.button</I18n>
                    </ButtonLabel>
                </IconButton>
            </SimpleDropdownItem>
        );
        const items = [
            <SimpleDropdownItem
                key="pdf"
                id={"pdf"}
                disabled={!widgetsLoaded}
            >
                <Flex
                    alignItems="center"
                    gap="4px"
                    padding="4px 12px"
                >
                    <Pdf
                        size="md"
                        color={colorsPalettes.carbon[200]}
                    />
                    {t("dashboard.header.export.pdf")}
                </Flex>
            </SimpleDropdownItem>,
        ];
        if (hasAccessToPptExport) {
            items.push(
                <SimpleDropdownItem
                    key="ppt"
                    id={"ppt"}
                    disabled={!widgetsLoaded}
                >
                    <Flex
                        alignItems="center"
                        gap="4px"
                        padding="4px 12px"
                    >
                        <Ppt
                            size="md"
                            color={colorsPalettes.carbon[200]}
                        />
                        {t("dashboard.header.export.ppt")}
                    </Flex>
                </SimpleDropdownItem>,
            );
        }
        return [menuButton, ...items];
    }

    const RenderExportMenuButton = () => (
        <Dropdown
            onClick={onExportMenuItemClick}
            onToggle={noop}
            itemsComponent={EllipsisDropdownItem}
            dropdownPopupPlacement="bottom-right"
            autoPlacement={false}
            width={220}
            buttonWidth={106}
            appendTo="body"
            cssClassContainer="DropdownContent-container DropdownContent-container--ellipsis-dashboard-header"
        >
            {getExportMenuItems()}
        </Dropdown>
    );

    const onElipsisMenuItemClick = (item) => {
        switch (item.id) {
            case "duplicate":
                SwTrack.all.trackEvent("Duplicate Report", "click", "Dashboard Header/Duplicate");
                duplicateDashboard();
                break;

            case "erase":
                SwTrack.all.trackEvent("Dashboard", "click", "Dashboard Header/Delete");
                setDeletePopUpIsOpen(true);
                break;
            case "rename":
                SwTrack.all.trackEvent("Dashboard", "click", "Dashboard Header/Rename");
                setIsRenameDialogOpen(true);
                break;
        }
    };

    function onCancel() {
        SwTrack.all.trackEvent("Dashboard", "click", "Dashboard Header/Delete/No");
        setDeletePopUpIsOpen(false);
    }
    function onConfirm() {
        SwTrack.all.trackEvent("Dashboard", "click", "Dashboard Header/Delete/Yes");
        setDeletePopUpIsOpen(false);
        deleteDashboard();
    }

    async function deleteDashboard() {
        const success = () => {
            if (DashboardService.dashboards.length) {
                const dashboard = DashboardService.getFirstDashboard();
                if (dashboard.name && dashboard.name === "newdashboard") {
                    services.swNavigator.go("dashboard-new"); // if the last item is the "add dashboard" button
                } else {
                    services.swNavigator.go("dashboard-exist", {
                        dashboardId: dashboard.id,
                    });
                    setTimeout(() => {
                        dispatch(setSharedWithMeDashboards());
                    });
                }
            } else {
                services.swNavigator.go("dashboard-new");
            }
        };

        try {
            if (isSharedByMe) {
                const { err, success } = await ShareDashboardService.unShare({
                    dashboardId: dashboard.id,
                });
                if (err || !success) {
                    fail();
                    return;
                }
            }
            await DashboardService.deleteDashboard({ id: dashboard.id });
            success();
        } catch {
            fail();
        }
    }

    function getElipsisMenuItem() {
        return [
            <SimpleDropdownItem
                key={"main-item"}
                id={"elipsesDropdown"}
            >
                <EllipsisIconContainer key={"dashboardHeaderEllipsis"}>
                    <IconButton
                        key={"dashboardHeaderEllipsisButton"}
                        type="flat"
                        iconName="dots-more"
                    />
                </EllipsisIconContainer>
            </SimpleDropdownItem>,
            <SimpleDropdownItem
                id="rename"
                key="rename"
                disabled={isSharedWithMe}
            >
                <IconItem>
                    <SWReactIcons iconName="edit" />
                    {"Rename"}
                </IconItem>
            </SimpleDropdownItem>,
            <SimpleDropdownItem
                id="duplicate"
                key="duplicate"
                disabled={!isDuplicatingEnabled}
            >
                <IconItem>
                    <SWReactIcons iconName="copy" />
                    {t("dashboard.header.ellipsis.duplicate")}
                </IconItem>
            </SimpleDropdownItem>,
            <SimpleDropdownItem
                id="erase"
                key="erase"
                disabled={isSharedWithMe}
            >
                <IconItem>
                    <SWReactIcons iconName="delete" />
                    {t("dashboard.header.ellipsis.delete")}
                </IconItem>
            </SimpleDropdownItem>,
        ];
    }

    const renderElipsisMenuButton = (): JSX.Element => {
        return (
            <EllipsisDashboardHeaderContainer
                onCancel={onCancel}
                onConfirm={onConfirm}
                isSharedByMe={isSharedByMe}
                deletePopupIsOpen={deletePopUpIsOpen}
                onToggle={noop}
                onClick={onElipsisMenuItemClick}
                translations={{
                    default: {
                        title: t("sidenav.delete.tooltip.title"),
                        subtitle: t("sidenav.delete.tooltip.subtitle"),
                        buttonText: t("sidenav.delete.tooltip.buttonText"),
                        cancelText: t("sidenav.delete.tooltip.cancelText"),
                    },
                    unshare: {
                        title: t("sidenav.delete.unshare.tooltip.title"),
                        subtitle: t("sidenav.delete.unshare.tooltip.subtitle"),
                        buttonText: t("sidenav.delete.unshare.tooltip.buttonText"),
                        cancelText: t("sidenav.delete.unshare.tooltip.cancelText"),
                    },
                }}
            >
                {deletePopUpIsOpen
                    ? [
                          <EllipsisIconContainer key={"iconConteainer"}>
                              <IconButton
                                  type="flat"
                                  iconName="dots-more"
                              />
                          </EllipsisIconContainer>,
                      ]
                    : getElipsisMenuItem()}
            </EllipsisDashboardHeaderContainer>
        );
    };

    const renderDasboardButtons = (): JSX.Element => (
        <>
            {renderElipsisMenuButton()}
            <RenderExportMenuButton />
            {isSharingEnabled ? <ShareButton /> : null}
            {renderSubscribeButton()}
            {isModalOpen ? renderSubscribeConfirmModal() : null}
            {renderAddMetricButton()}
        </>
    );

    return (
        <DashboardHeaderContainer
            className="dashboard-header"
            isTrial={isTrial.current}
        >
            {renderHeaderTitle()}
            <DashboardButtons>{renderDasboardButtons()}</DashboardButtons>
            <DashboardRenameDialog
                isOpen={isRenameDialogOpen}
                onClose={revertDashboardName}
                onRename={renameDashboard}
                currentName={currentTitle}
            />
        </DashboardHeaderContainer>
    );
};
